import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import SystemAdminApi from "../../../infrastructure/api/systemAdmin";

const pick = (obj, camel, pascal, fallback = null) =>
  obj?.[camel] ?? obj?.[pascal] ?? fallback;

const formatBytes = (bytes) => {
  if (bytes == null || Number.isNaN(Number(bytes))) return "—";
  const n = Number(bytes);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const sourceLabel = (source) => {
  const s = (source || "").toLowerCase();
  if (s === "environment") return "متغیر محیطی";
  if (s === "ui") return "تنظیمات این صفحه";
  if (s === "appsettings") return "appsettings";
  if (s === "sqldefault") return "پیش‌فرض SQL Server";
  return source || "—";
};

const AdminDatabaseBackupCard = () => {
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [directory, setDirectory] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState(null);
  const [result, setResult] = useState(null);

  const lockedByEnv = pick(settings, "isLockedByEnvironment", "IsLockedByEnvironment", false);
  const sqlDefault = pick(settings, "sqlServerDefaultDirectory", "SqlServerDefaultDirectory");
  const effectiveDir = pick(settings, "effectiveDirectory", "EffectiveDirectory");
  const effectiveSource = pick(settings, "effectiveSource", "EffectiveSource");

  const loadSettings = useCallback(async () => {
    setSettingsLoading(true);
    setError(null);
    try {
      const res = await SystemAdminApi.getDatabaseBackupSettings();
      const normalized = SystemAdminApi.normalize(res, "بارگذاری تنظیمات بکاپ ناموفق بود.");
      if (normalized.error) {
        setError(normalized.error);
        return;
      }
      setSettings(normalized.data);
      setDirectory(pick(normalized.data, "directory", "Directory", "") || "");
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری تنظیمات");
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const onSavePath = async () => {
    setSaveLoading(true);
    setError(null);
    setSaveMessage(null);
    try {
      const res = await SystemAdminApi.saveDatabaseBackupSettings(directory);
      const normalized = SystemAdminApi.normalize(res, "ذخیره مسیر ناموفق بود.");
      if (normalized.error) {
        setError(normalized.error);
        return;
      }
      setSettings(normalized.data);
      setDirectory(pick(normalized.data, "directory", "Directory", "") || "");
      setSaveMessage(
        res?.message || res?.Message || "مسیر پشتیبان‌گیری ذخیره شد."
      );
    } catch (e) {
      setError(e?.message || "خطا در ذخیره مسیر");
    } finally {
      setSaveLoading(false);
    }
  };

  const onBackup = async () => {
    setBackupLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await SystemAdminApi.createDatabaseBackup();
      const normalized = SystemAdminApi.normalize(res, "پشتیبان‌گیری ناموفق بود.");
      if (normalized.error) {
        setError(normalized.error);
        return;
      }
      setResult(normalized.data);
      await loadSettings();
    } catch (e) {
      setError(e?.message || "خطا در پشتیبان‌گیری");
    } finally {
      setBackupLoading(false);
    }
  };

  const success = pick(result, "success", "Success", false);
  const persianDate = pick(result, "persianDate", "PersianDate");
  const fileName = pick(result, "fileName", "FileName");
  const filePath = pick(result, "filePath", "FilePath");
  const databaseName = pick(result, "databaseName", "DatabaseName");
  const fileSize = pick(result, "fileSizeBytes", "FileSizeBytes");
  const envDir = pick(settings, "environmentDirectory", "EnvironmentDirectory");

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <BackupOutlinedIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={800}>
            پشتیبان‌گیری دستی پایگاه داده
          </Typography>
        </Stack>

        {settingsLoading ? (
          <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Stack spacing={2}>
            {lockedByEnv ? (
              <Alert severity="warning">
                مسیر از متغیر محیطی <strong>SOLARENT_BACKUP_DIRECTORY</strong> خوانده
                می‌شود و از این صفحه قابل تغییر نیست: {envDir || "—"}
              </Alert>
            ) : null}

            <TextField
              label="مسیر ذخیره فایل بکاپ (روی سرور SQL)"
              placeholder={
                sqlDefault
                  ? `خالی = پیش‌فرض SQL: ${sqlDefault}`
                  : "مثال: D:\\SolarentBackups"
              }
              value={directory}
              onChange={(e) => setDirectory(e.target.value)}
              fullWidth
              disabled={lockedByEnv || saveLoading || backupLoading}
              helperText="مسیر روی دیسک سرور SQL است. اگر پوشه وجود نداشته باشد، قبل از بکاپ ساخته می‌شود (مثال: E:\backup یا E:\backup\14050316). خالی = پیش‌فرض SQL."
              dir="ltr"
              InputProps={{ sx: { fontFamily: "monospace", fontSize: "0.9rem" } }}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                disabled={lockedByEnv || saveLoading || backupLoading}
                onClick={onSavePath}
                startIcon={
                  saveLoading ? <CircularProgress size={16} /> : <SaveOutlinedIcon />
                }
              >
                ذخیره مسیر
              </Button>
              <Button
                variant="contained"
                color="primary"
                disabled={backupLoading || settingsLoading}
                onClick={onBackup}
                startIcon={
                  backupLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <BackupOutlinedIcon />
                  )
                }
              >
                {backupLoading ? "در حال پشتیبان‌گیری…" : "گرفتن بکاپ الان"}
              </Button>
            </Stack>

            {effectiveDir ? (
              <Typography variant="body2" color="text.secondary">
                مسیر مؤثر در اجرای بعدی:{" "}
                <Typography component="span" fontFamily="monospace" fontSize="0.85rem">
                  {effectiveDir}
                </Typography>
                {" "}
                ({sourceLabel(effectiveSource)})
              </Typography>
            ) : null}

            {sqlDefault && !directory && !lockedByEnv ? (
              <Typography variant="caption" color="text.secondary" display="block">
                پوشه پیش‌فرض SQL: {sqlDefault}
              </Typography>
            ) : null}
          </Stack>
        )}

        {saveMessage ? (
          <Alert severity="success" sx={{ mt: 2 }}>
            {saveMessage}
          </Alert>
        ) : null}

        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}

        {success && result ? (
          <Alert severity="success" icon={<CheckCircleOutlineIcon />} sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight={700} gutterBottom>
              {pick(result, "message", "Message", "پشتیبان‌گیری انجام شد.")}
            </Typography>
            <Typography variant="body2">
              پایگاه: {databaseName || "—"} · تاریخ شمسی: {persianDate || "—"}
            </Typography>
            <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
              فایل: {fileName || "—"} ({formatBytes(fileSize)})
            </Typography>
            {filePath ? (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ mt: 0.5 }}
              >
                مسیر: {filePath}
              </Typography>
            ) : null}
          </Alert>
        ) : null}
      </CardContent>
    </Card>
  );
};

export default AdminDatabaseBackupCard;
